import React, { useCallback, useEffect, useRef } from 'react';

import { injectStyleIntoDocuments } from '../inject-style-into-documents';
import { removeStyleElements } from '../remove-style-elements';
import { watchForIframeChanges } from '../watch-for-iframe-changes';
import { type StyleRendererProps } from './types';

/**
 * StyleRenderer Component
 *
 * Renders pre-generated CSS styles for blocks in the editor.
 * Blocks are responsible for generating their own CSS.
 */
export const StyleRenderer: React.FC< StyleRendererProps > = ( {
	css,
	blockId,
	children,
} ) => {
	const styleElementsRef = useRef< HTMLStyleElement[] >( [] );

	const injectStyles = useCallback( () => {
		if ( ! css ) return;
		// Remove old styles and inject new ones
		removeStyleElements( styleElementsRef.current );
		styleElementsRef.current = injectStyleIntoDocuments( css, blockId );
	}, [ css, blockId ] );

	useEffect( () => {
		if ( ! css ) {
			// Remove existing styles if no CSS provided
			removeStyleElements( styleElementsRef.current );
			styleElementsRef.current = [];
			return;
		}

		// Initial injection
		injectStyles();

		// Watch for iframe changes and re-inject styles
		const stopWatching = watchForIframeChanges( injectStyles );

		// Cleanup function
		return () => {
			removeStyleElements( styleElementsRef.current );
			styleElementsRef.current = [];
			stopWatching();
		};
	}, [ css, blockId, injectStyles ] );

	// Render children with data attribute for CSS targeting
	return children ? <div data-block-id={ blockId }>{ children }</div> : null;
};
